import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { FaQrcode } from 'react-icons/fa';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { DataTableColumnHeader } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTableColumnHeader';
import { OverlayPopover } from '../../components/OverlayPopover';
import { getContainerQrCodeImgUrl } from '@/utils/qrCode';
import { ContainerLocationStatus } from '@/utils/containerLocation';
import { CurrencyById } from '@/utils/price';

/** A single container the user currently holds (one open checkout). */
export type UserHolding = {
  id: number;
  containerId: string;
  containerName: string;
  ownerSince: string;
  returnDeadline: string | null;
  status: ContainerLocationStatus;
  /** Set when the holding was already charged to the partner as overdue. */
  chargedAmount: number | null;
  chargedCurrencyId: number | null;
  chargedTimestamp: string | null;
};

/** Statuses that should be flagged (overdue / lost / broken). */
const ALERT_STATUSES = new Set<ContainerLocationStatus>([
  ContainerLocationStatus.Overdue,
  ContainerLocationStatus.OverdueSold,
  ContainerLocationStatus.OverdueError,
  ContainerLocationStatus.OverdueNoCharge,
  ContainerLocationStatus.Lost,
  ContainerLocationStatus.LostByStore,
  ContainerLocationStatus.Broken,
  ContainerLocationStatus.BrokenAtUser,
]);

/**
 * Charge-centric status badge for the "containers with the user" list: says
 * whether the holding will be, was, or won't be charged rather than exposing
 * the raw location status. Unexpected statuses fall back to the enum name.
 */
function renderHoldingStatusBadge(holding: UserHolding) {
  const deadlinePassed =
    holding.returnDeadline != null &&
    new Date(holding.returnDeadline).getTime() < Date.now();

  switch (holding.status) {
    case ContainerLocationStatus.CheckedOut:
      return deadlinePassed ? (
        <Badge variant="destructive">
          {i18n.t('MerchantTransactions:holdingStatusWillBeCharged')}
        </Badge>
      ) : (
        <Badge variant="secondary">
          {i18n.t('MerchantTransactions:holdingStatusActive')}
        </Badge>
      );
    case ContainerLocationStatus.Overdue:
      return (
        <Badge variant="destructive">
          {i18n.t('MerchantTransactions:holdingStatusWillBeCharged')}
        </Badge>
      );
    case ContainerLocationStatus.OverdueNoCharge:
      return (
        <Badge variant="secondary">
          {i18n.t('MerchantTransactions:holdingStatusExempted')}
        </Badge>
      );
    case ContainerLocationStatus.OverdueSold:
      return (
        <Badge variant="secondary">
          {i18n.t('MerchantTransactions:holdingStatusCharged')}
        </Badge>
      );
    default:
      return (
        <Badge
          variant={
            ALERT_STATUSES.has(holding.status) ? 'destructive' : 'secondary'
          }
        >
          {ContainerLocationStatus[holding.status]}
        </Badge>
      );
  }
}

function formatChargedAmount(amount: number, currencyId: number | null) {
  const currency = (CurrencyById as Record<number, string>)[currencyId ?? 0];
  if (!currency || currency === 'Undefined') {
    return amount.toFixed(2);
  }
  return new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency,
  }).format(amount);
}

function renderContainerNameCell(
  containerId: string,
  containerName: string,
  canAccessPartnerData: boolean,
) {
  if (!canAccessPartnerData) {
    return containerName;
  }

  return (
    <Link to={`/container/${containerId}`}>
      {containerName}
      <OverlayPopover
        body={
          <img
            alt="QR code for container"
            src={getContainerQrCodeImgUrl(containerId)}
          />
        }
        header={i18n.t('MerchantTransactions:QRCodeFor', {
          name: containerName,
        })}
      >
        <FaQrcode className="ms-1" style={{ cursor: 'pointer' }} />
      </OverlayPopover>
    </Link>
  );
}

/**
 * The real "Return" action: hands the container back to the store. Shows a
 * spinner and is disabled while this row's return is in flight.
 */
function renderReturnAction(
  holding: UserHolding,
  onReturn: (holding: UserHolding) => void,
  isReturning: boolean,
) {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isReturning}
      onClick={() => onReturn(holding)}
    >
      {isReturning && <Spinner className="tw:mr-2" />}
      {isReturning
        ? i18n.t('MerchantTransactions:returning')
        : i18n.t('MerchantTransactions:returnContainer')}
    </Button>
  );
}

/**
 * The "Don't charge" action for already-charged holdings: exempts the charge
 * from the upcoming partner invoice while the container stays with the user.
 */
function renderDontChargeAction(
  holding: UserHolding,
  onDontCharge: (holding: UserHolding) => void,
  isExempting: boolean,
) {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isExempting}
      onClick={() => onDontCharge(holding)}
    >
      {isExempting && <Spinner className="tw:mr-2" />}
      {isExempting
        ? i18n.t('MerchantTransactions:dontCharging')
        : i18n.t('MerchantTransactions:dontCharge')}
    </Button>
  );
}

export type GetCheckoutsByUserColumnsOptions = {
  canAccessPartnerData: boolean;
  /** Opens the confirm flow for returning a container. */
  onReturn: (holding: UserHolding) => void;
  /** Id of the holding whose return is currently in flight, if any. */
  returningId: number | null;
  /**
   * 'withUser' shows the charge-status badge; 'onInvoice' (already-charged
   * holdings of the current month) shows charge date and amount instead.
   */
  variant: 'withUser' | 'onInvoice';
  /** Opens the confirm flow for exempting a charge ('onInvoice' rows only). */
  onDontCharge?: (holding: UserHolding) => void;
  /** Id of the holding whose exemption is currently in flight, if any. */
  dontChargingId?: number | null;
};

export function getCheckoutsByUserColumns({
  canAccessPartnerData,
  onReturn,
  returningId,
  variant,
  onDontCharge,
  dontChargingId,
}: GetCheckoutsByUserColumnsOptions): ColumnDef<UserHolding>[] {
  const variantColumns: ColumnDef<UserHolding>[] =
    variant === 'withUser'
      ? [
          {
            accessorKey: 'status',
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title={i18n.t('MerchantTransactions:status')}
              />
            ),
            cell: ({ row }) => renderHoldingStatusBadge(row.original),
            meta: {
              getName: () => i18n.t('MerchantTransactions:status'),
            },
          },
        ]
      : [
          {
            accessorKey: 'chargedTimestamp',
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title={i18n.t('MerchantTransactions:chargedOn')}
              />
            ),
            cell: ({ row }) =>
              row.original.chargedTimestamp ? (
                <span title={row.original.chargedTimestamp}>
                  {new Date(row.original.chargedTimestamp).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true),
                  )}
                </span>
              ) : (
                '-'
              ),
            meta: {
              getName: () => i18n.t('MerchantTransactions:chargedOn'),
            },
          },
          {
            accessorKey: 'chargedAmount',
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title={i18n.t('MerchantTransactions:chargedAmount')}
              />
            ),
            cell: ({ row }) =>
              row.original.chargedAmount != null
                ? formatChargedAmount(
                    row.original.chargedAmount,
                    row.original.chargedCurrencyId,
                  )
                : '-',
            meta: {
              getName: () => i18n.t('MerchantTransactions:chargedAmount'),
            },
          },
        ];

  return [
    {
      accessorKey: 'containerName',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:containerName')}
        />
      ),
      cell: ({ row }) =>
        renderContainerNameCell(
          row.original.containerId,
          row.original.containerName,
          canAccessPartnerData,
        ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:containerName'),
      },
    },
    {
      accessorKey: 'ownerSince',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:checkoutDate')}
        />
      ),
      cell: ({ row }) => (
        <span title={row.original.ownerSince}>
          {new Date(row.original.ownerSince).toLocaleDateString(
            i18n.language,
            getDateFormatOptions(true),
          )}
        </span>
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:checkoutDate'),
      },
    },
    {
      accessorKey: 'returnDeadline',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:returnDeadline')}
        />
      ),
      cell: ({ row }) =>
        row.original.returnDeadline ? (
          <span title={row.original.returnDeadline}>
            {new Date(row.original.returnDeadline).toLocaleDateString(
              i18n.language,
              getDateFormatOptions(true),
            )}
          </span>
        ) : (
          '-'
        ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:returnDeadline'),
      },
    },
    ...variantColumns,
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      header: () => <div>{i18n.t('MerchantTransactions:actions')}</div>,
      cell: ({ row }) => (
        <div className="tw:flex tw:gap-2">
          {renderReturnAction(
            row.original,
            onReturn,
            returningId === row.original.id,
          )}
          {variant === 'onInvoice' &&
            onDontCharge &&
            renderDontChargeAction(
              row.original,
              onDontCharge,
              dontChargingId === row.original.id,
            )}
        </div>
      ),
    },
  ];
}
